/**
 * Document File Types - Single Source of Truth
 *
 * This module provides Zod-based enum validation for document file types.
 * All file type validation should import from this module to ensure consistency.
 */
import { z } from 'zod';

// ============================================================================
// ENUMS - Single Source of Truth
// ============================================================================

/**
 * File categories for document uploads
 */
export const FileCategorySchema = z.enum(['pdf', 'word', 'excel', 'powerpoint', 'video']);
export type FileCategory = z.infer<typeof FileCategorySchema>;

/**
 * Allowed document file extensions (without dots)
 */
export const DocumentExtensionSchema = z.enum([
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'xlsm',
  // Video formats
  'mp4',
  'webm',
  'mpeg',
  'mpg',
  'mov',
  'wmv',
  '3gp',
  'flv',
]);
export type DocumentExtension = z.infer<typeof DocumentExtensionSchema>;

/**
 * Allowed document MIME types
 */
export const DocumentMimeTypeSchema = z.enum([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  // Video MIME types
  'video/mp4',
  'video/webm',
  'video/mpeg',
  'video/quicktime',
  'video/x-ms-wmv',
  'video/3gpp',
  'video/x-flv',
]);
export type DocumentMimeType = z.infer<typeof DocumentMimeTypeSchema>;

// ============================================================================
// SIZE LIMITS
// ============================================================================

const BYTES_PER_MB = 1024 * 1024;

/**
 * Maximum file size limits in megabytes per document category
 */
export const FILE_CATEGORY_SIZE_LIMITS_MB: Record<FileCategory, number> = {
  pdf: 250,
  word: 200,
  excel: 200,
  powerpoint: 200,
  video: 500,
};

/**
 * Maximum file size limits in bytes per document category
 */
export const FILE_CATEGORY_SIZE_LIMITS_BYTES: Record<FileCategory, number> = {
  pdf: FILE_CATEGORY_SIZE_LIMITS_MB.pdf * BYTES_PER_MB,
  word: FILE_CATEGORY_SIZE_LIMITS_MB.word * BYTES_PER_MB,
  excel: FILE_CATEGORY_SIZE_LIMITS_MB.excel * BYTES_PER_MB,
  powerpoint: FILE_CATEGORY_SIZE_LIMITS_MB.powerpoint * BYTES_PER_MB,
  video: FILE_CATEGORY_SIZE_LIMITS_MB.video * BYTES_PER_MB,
};

// ============================================================================
// MAPPINGS - Extension and MIME Type to Category
// ============================================================================

/**
 * Maps document extensions to their file category
 */
export const EXTENSION_TO_CATEGORY: Record<DocumentExtension, FileCategory> = {
  'pdf': 'pdf',
  'doc': 'word',
  'docx': 'word',
  'ppt': 'powerpoint',
  'pptx': 'powerpoint',
  'xls': 'excel',
  'xlsx': 'excel',
  'xlsm': 'excel',
  // Video extensions
  'mp4': 'video',
  'webm': 'video',
  'mpeg': 'video',
  'mpg': 'video',
  'mov': 'video',
  'wmv': 'video',
  '3gp': 'video',
  'flv': 'video',
};

/**
 * Maps document MIME types to their file category
 */
export const MIME_TYPE_TO_CATEGORY: Record<DocumentMimeType, FileCategory> = {
  'application/pdf': 'pdf',
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.ms-powerpoint': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12': 'excel',
  // Video MIME types
  'video/mp4': 'video',
  'video/webm': 'video',
  'video/mpeg': 'video',
  'video/quicktime': 'video',
  'video/x-ms-wmv': 'video',
  'video/3gpp': 'video',
  'video/x-flv': 'video',
};

/**
 * Maps MIME types to their file extensions (with dots)
 */
export const MIME_TYPE_TO_EXTENSIONS: Record<DocumentMimeType, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  // Video MIME types
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/mpeg': ['.mpeg', '.mpg'],
  'video/quicktime': ['.mov'],
  'video/x-ms-wmv': ['.wmv'],
  'video/3gpp': ['.3gp'],
  'video/x-flv': ['.flv'],
};

// ============================================================================
// DERIVED CONSTANTS
// ============================================================================

/**
 * All allowed document extensions (without dots)
 */
export const ALLOWED_EXTENSIONS = DocumentExtensionSchema.options;

/**
 * All allowed document extensions (with dots)
 */
export const ALLOWED_EXTENSIONS_WITH_DOTS = ALLOWED_EXTENSIONS.map(ext => `.${ext}`);

/**
 * All allowed document MIME types
 */
export const ALLOWED_MIME_TYPES = DocumentMimeTypeSchema.options;

/**
 * Document-only MIME types (excluding video)
 */
export const ALLOWED_MIME_TYPES_DOCUMENTS_ONLY = ALLOWED_MIME_TYPES.filter(
  mimeType => MIME_TYPE_TO_CATEGORY[mimeType] !== 'video',
);

/**
 * Accept string for file input elements
 */
export const DOCUMENT_ACCEPT_STRING = ALLOWED_EXTENSIONS_WITH_DOTS.join(', ');

/**
 * Document-only extensions (excluding video formats)
 */
export const DOCUMENT_ONLY_EXTENSIONS_WITH_DOTS = ALLOWED_EXTENSIONS_WITH_DOTS.filter(
  (ext) => {
    const bare = ext.replace('.', '') as DocumentExtension;
    return EXTENSION_TO_CATEGORY[bare] !== 'video';
  },
);

/**
 * Accept string for file input elements (documents only, no video)
 */
export const DOCUMENT_ONLY_ACCEPT_STRING = DOCUMENT_ONLY_EXTENSIONS_WITH_DOTS.join(', ');

/**
 * Maximum file size across all document types (for fallback)
 */
export const MAX_DOCUMENT_SIZE_MB = Math.max(...Object.values(FILE_CATEGORY_SIZE_LIMITS_MB));

/**
 * Minimum file size across all document types (for fallback)
 */
export const MIN_DOCUMENT_SIZE_MB = Math.min(...Object.values(FILE_CATEGORY_SIZE_LIMITS_MB));

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Extracts the file extension from a filename (lowercase, without dot)
 */
export function extractExtension(fileName: string): string | null {
  const parts = fileName.split('.');
  if (parts.length < 2) {
    return null;
  }
  const extension = parts.pop();
  return extension !== undefined ? extension.toLowerCase() : null;
}

/**
 * Validates and parses a file extension
 */
export function parseExtension(extension: string): DocumentExtension | null {
  const result = DocumentExtensionSchema.safeParse(extension.toLowerCase());
  return result.success === true ? result.data : null;
}

/**
 * Validates and parses a MIME type
 */
export function parseMimeType(mimeType: string): DocumentMimeType | null {
  const result = DocumentMimeTypeSchema.safeParse(mimeType);
  return result.success === true ? result.data : null;
}

/**
 * Gets the file category from an extension
 */
export function getCategoryFromExtension(extension: string): FileCategory | null {
  const parsed = parseExtension(extension);
  return parsed !== null ? EXTENSION_TO_CATEGORY[parsed] : null;
}

/**
 * Gets the file category from a MIME type
 */
export function getCategoryFromMimeType(mimeType: string): FileCategory | null {
  const parsed = parseMimeType(mimeType);
  return parsed !== null ? MIME_TYPE_TO_CATEGORY[parsed] : null;
}

/**
 * Gets the file category from either MIME type or filename
 */
export function getFileCategory(
  mimeType: string | undefined,
  fileName: string,
): FileCategory | null {
  // Try MIME type first
  if (mimeType !== undefined && mimeType !== '') {
    const category = getCategoryFromMimeType(mimeType);
    if (category !== null) {
      return category;
    }
  }

  // Fall back to extension
  const extension = extractExtension(fileName);
  if (extension !== null) {
    return getCategoryFromExtension(extension);
  }

  return null;
}

/**
 * Gets the maximum file size in bytes for a file
 */
export function getMaxFileSizeBytes(
  mimeType: string | undefined,
  fileName: string,
): number {
  const category = getFileCategory(mimeType, fileName);
  if (category === null) {
    return MIN_DOCUMENT_SIZE_MB * BYTES_PER_MB;
  }
  return FILE_CATEGORY_SIZE_LIMITS_BYTES[category];
}

/**
 * Gets the maximum file size in MB for a file
 */
export function getMaxFileSizeMB(
  mimeType: string | undefined,
  fileName: string,
): number {
  const category = getFileCategory(mimeType, fileName);
  if (category === null) {
    return MIN_DOCUMENT_SIZE_MB;
  }
  return FILE_CATEGORY_SIZE_LIMITS_MB[category];
}

// ============================================================================
// FILE VALIDATION SCHEMA
// ============================================================================

/**
 * File validation input schema
 */
export const FileValidationInputSchema = z.object({
  name: z.string().min(1),
  size: z.number().int().positive(),
  type: z.string().optional(),
});
export type FileValidationInput = z.infer<typeof FileValidationInputSchema>;

/**
 * File validation result schema
 */
export const FileValidationResultSchema = z.object({
  valid: z.boolean(),
  error: z.string().optional(),
  maxSizeMB: z.number(),
  category: FileCategorySchema.nullable(),
});
export type FileValidationResult = z.infer<typeof FileValidationResultSchema>;

/**
 * Validates a file against type-specific size limits using Zod
 */
export function validateFileSizeByType(
  input: FileValidationInput,
): FileValidationResult {
  const category = getFileCategory(input.type, input.name);
  const maxSizeMB = category !== null
    ? FILE_CATEGORY_SIZE_LIMITS_MB[category]
    : MIN_DOCUMENT_SIZE_MB;
  const maxSizeBytes = maxSizeMB * BYTES_PER_MB;
  const fileSizeMB = input.size / BYTES_PER_MB;

  if (input.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size (${fileSizeMB.toFixed(2)} MB) exceeds the maximum allowed (${maxSizeMB} MB)`,
      maxSizeMB,
      category,
    };
  }

  return {
    valid: true,
    maxSizeMB,
    category,
  };
}

// ============================================================================
// REACT-DROPZONE ACCEPT OBJECT
// ============================================================================

/**
 * Generate accept object for react-dropzone
 */
export function getDropzoneAcceptObject(): Record<DocumentMimeType, string[]> {
  return Object.fromEntries(
    ALLOWED_MIME_TYPES.map(mimeType => [mimeType, MIME_TYPE_TO_EXTENSIONS[mimeType]]),
  ) as Record<DocumentMimeType, string[]>;
}
